/* eslint-disable react/display-name */
import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import CheckRounded from '@mui/icons-material/CheckRounded'
import DoneAllRounded from '@mui/icons-material/DoneAllRounded'
import CloseRounded from '@mui/icons-material/CloseRounded'
import { ConfirmDialog } from '../components/UI/RowActionMenu'
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded'
import SoupKitchenRounded from '@mui/icons-material/SoupKitchenRounded'
import DeliveryDiningRounded from '@mui/icons-material/DeliveryDiningRounded'
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded'
import StatusBadge from '../components/StatusBadge'
import { StatTile } from '../components/UI/Depth'
import { useQuery, useMutation, useSubscription, gql } from '@apollo/client'
import DataTable from '../components/DataTable'
import { getActiveOrders, subscriptionOrder, updateStatus } from '../apollo'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import { transformToNewline } from '../utils/stringManipulations'
import SearchBar from '../components/TableHeader/SearchBar'
import { useParams } from 'react-router-dom'
import { wideTableStyles } from '../utils/tableCustomStyles'
import useGlobalStyles from '../utils/globalStyles'
import TableHeader from '../components/TableHeader'
import PrepTimeCountdown from '../components/Order/PrepTimeCountdown'
import OrderNotes from '../components/Order/OrderNotes'
import AcceptOrderDialog from '../components/Order/AcceptOrderDialog'

const SUBSCRIPTION_ORDER = gql`
  ${subscriptionOrder}
`
const UPDATE_STATUS = gql`
  ${updateStatus}
`
const GET_ACTIVE_ORDERS = gql`
  ${getActiveOrders}
`

const DispatchRestaurant = props => {
  const { t } = props
  const params = useParams()

  const [restaurantId, seteRestaurantId] = useState(
    localStorage.getItem('restaurantId')
  )
  useEffect(() => {
    if (params.id) seteRestaurantId(params.id)
  }, [])
  const [searchQuery, setSearchQuery] = useState('')
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const [mutateUpdate, { loading: updating }] = useMutation(UPDATE_STATUS)
  // Order waiting on the preparation-time dialog before it is accepted
  const [accepting, setAccepting] = useState(null)
  const globalClasses = useGlobalStyles()

  const {
    data: dataOrders,
    error: errorOrders,
    loading: loadingOrders,
    refetch: refetchOrders
  } = useQuery(GET_ACTIVE_ORDERS, {
    variables: { restaurantId },
    pollInterval: 3000,
    skip: restaurantId === null
  })

  const [rejecting, setRejecting] = useState(null)
  const [rejectLoading, setRejectLoading] = useState(false)
  const setStatus = (row, orderStatus, time) => {
    if (
      orderStatus === 'ACCEPTED' &&
      time === undefined &&
      row.deliveryType !== 'scheduled'
    ) {
      setAccepting(row)
      return
    }
    return mutateUpdate({ variables: { id: row._id, orderStatus, time } })
  }

  const confirmReject = async () => {
    if (!rejecting) return
    setRejectLoading(true)
    try {
      await setStatus(rejecting, 'CANCELLED')
      setRejecting(null)
    } finally {
      setRejectLoading(false)
    }
  }

  // Soft tinted pill: colour carries the meaning without shouting like a
  // gradient block does in every row.
  const actionSx = (fg, bg) => ({
    minWidth: 0,
    height: 28,
    px: 1.25,
    gap: 0.5,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    color: fg,
    bgcolor: bg,
    '& .MuiButton-startIcon': { mr: 0.25, ml: 0 },
    '&:hover': { bgcolor: bg, filter: 'brightness(0.96)' }
  })

  // Badge on top, then one line of actions: the next step as a labelled
  // button, rejecting as a small icon (it asks first).
  const statusFunc = row => {
    const open = ['PENDING', 'ACCEPTED', 'PICKED', 'ASSIGNED'].includes(
      row.orderStatus
    )
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.75,
          py: 1
        }}>
        <StatusBadge status={row.orderStatus} />
        {open ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {row.orderStatus === 'PENDING' ? (
              <Button
                size="small"
                startIcon={
                  <CheckRounded sx={{ fontSize: '16px !important' }} />
                }
                sx={actionSx('#15803D', '#DCFCE7')}
                onClick={() => setStatus(row, 'ACCEPTED')}>
                {t('Accept')}
              </Button>
            ) : (
              <Button
                size="small"
                startIcon={
                  <DoneAllRounded sx={{ fontSize: '16px !important' }} />
                }
                sx={actionSx('#1D4ED8', '#DBEAFE')}
                onClick={() => setStatus(row, 'DELIVERED')}>
                {t('Delivered')}
              </Button>
            )}
            <Tooltip title={t('Reject')}>
              <IconButton
                size="small"
                aria-label={t('Reject')}
                onClick={() => setRejecting(row)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  color: '#B91C1C',
                  border: '1px solid #FECACA',
                  bgcolor: '#FFF5F5',
                  '&:hover': { bgcolor: '#FEE2E2' }
                }}>
                <CloseRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </Box>
    )
  }

  const subscribeFunc = row => {
    const { data: dataSubscription } = useSubscription(SUBSCRIPTION_ORDER, {
      variables: { id: row._id }
    })
    console.log(dataSubscription)
    return (
      <div style={{ overflow: 'visible', whiteSpace: 'pre' }}>
        {row.orderId}
        <br />
        {transformToNewline(row.deliveryAddress.deliveryAddress, 3)}
      </div>
    )
  }
  const columns = [
    {
      name: t('OrderInformation'),
      minWidth: '170px',
      wrap: true,
      sortable: true,
      selector: 'orderId',
      cell: row => subscribeFunc(row)
    },
    {
      name: t('RestaurantCol'),
      minWidth: '110px',
      wrap: true,
      selector: 'restaurant.name'
    },
    {
      name: t('Payment'),
      minWidth: '95px',
      wrap: true,
      selector: 'paymentMethod'
    },
    {
      name: t('Type'),
      minWidth: '95px',
      wrap: true,
      selector: 'isPickedUp',
      cell: row => (row.isPickedUp ? 'Pickup' : 'Delivery')
    },
    {
      name: t('Status'),
      minWidth: '170px',
      selector: 'orderStatus',
      cell: row => statusFunc(row)
    },
    {
      name: t('StoreNote'),
      selector: 'orderNotes',
      minWidth: '150px',
      wrap: true,
      cell: row => <OrderNotes order={row} type="store" />
    },
    {
      name: t('RiderNote'),
      selector: 'riderNotes',
      minWidth: '150px',
      wrap: true,
      cell: row => <OrderNotes order={row} type="rider" />
    },
    {
      name: t('PrepTime'),
      minWidth: '165px',
      wrap: true,
      selector: 'preparationTime',
      cell: row => <PrepTimeCountdown order={row} />
    },
    {
      name: t('OrderTime'),
      minWidth: '115px',
      wrap: true,
      cell: row => (
        <>{new Date(row.createdAt).toLocaleString().replace(/ /g, '\n')}</>
      )
    }
  ]

  const conditionalRowStyles = [
    {
      when: row => ['DELIVERED', 'CANCELLED'].includes(row.orderStatus),
      style: { opacity: 0.6 }
    }
  ]
  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null

  const filtered =
    searchQuery.length < 3
      ? dataOrders && dataOrders.getActiveOrders
      : dataOrders &&
        dataOrders.getActiveOrders.filter(order => {
          return (
            order.restaurant.name.toLowerCase().search(regex) > -1 ||
            order.orderId.toLowerCase().search(regex) > -1 ||
            order.deliveryAddress.deliveryAddress.toLowerCase().search(regex) >
              -1 ||
            order.orderId.toLowerCase().search(regex) > -1 ||
            order.paymentMethod.toLowerCase().search(regex) > -1 ||
            order.orderStatus.toLowerCase().search(regex) > -1 ||
            (order.rider !== null
              ? order.rider.name.toLowerCase().search(regex) > -1
              : false)
          )
        })

  const activeOrders = (dataOrders && dataOrders.getActiveOrders) || []
  const countOf = statuses =>
    activeOrders.filter(order => statuses.includes(order.orderStatus)).length
  const dispatchStats = [
    {
      label: t('Pending'),
      value: countOf(['PENDING']),
      icon: HourglassTopRounded,
      tone: 'amber'
    },
    {
      label: t('Accepted'),
      value: countOf(['ACCEPTED']),
      icon: SoupKitchenRounded,
      tone: 'blue'
    },
    {
      label: t('OnTheWay'),
      value: countOf(['ASSIGNED', 'PICKED']),
      icon: DeliveryDiningRounded,
      tone: 'violet'
    },
    {
      label: t('ActiveOrders'),
      value: activeOrders.length,
      icon: ReceiptLongRounded,
      tone: 'orange'
    }
  ]

  return (
    <>
      <AcceptOrderDialog
        open={Boolean(accepting)}
        loading={updating}
        onClose={() => setAccepting(null)}
        onConfirm={time => {
          setStatus(accepting, 'ACCEPTED', time)
          setAccepting(null)
        }}
      />
      <Header />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {errorOrders ? (
          <Typography sx={{ color: 'error.main', mb: 2 }}>
            {`${t('Error')} ${errorOrders.message}`}
          </Typography>
        ) : null}
        <Grid container spacing={2.5} mb={3}>
          {dispatchStats.map(stat => (
            <Grid item xs={6} lg={3} key={stat.label}>
              <StatTile {...stat} />
            </Grid>
          ))}
        </Grid>
        {loadingOrders ? (
          <CustomLoader />
        ) : (
          <DataTable
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                onClick={() => refetchOrders()}
              />
            }
            title={<TableHeader title={t('Dispatch')} />}
            columns={columns}
            data={filtered}
            progressPending={loadingOrders}
            pointerOnHover
            progressComponent={<CustomLoader />}
            pagination
            conditionalRowStyles={conditionalRowStyles}
            customStyles={wideTableStyles(1230)}
            selectableRows
          />
        )}
      </Container>
      <ConfirmDialog
        open={!!rejecting}
        title={t('RejectOrderTitle')}
        message={t('RejectOrderMessage', { id: rejecting?.orderId || '' })}
        confirmLabel={t('Reject')}
        cancelLabel={t('Cancel')}
        loading={rejectLoading}
        onConfirm={confirmReject}
        onClose={() => setRejecting(null)}
      />
    </>
  )
}
export default withTranslation()(DispatchRestaurant)
